import type {
  AccountingOperationDto,
  ServiceAccountingOperationDto,
  ServiceAccountingOperationType,
} from '@shared/api/contracts';
import { SERVICE_OPERATION_TYPES } from './constants';
import { parseServiceDate, toSafeIsoString } from './format-date';
import { parseWalletKind } from './wallet';

const OPERATION_TYPES = new Set<string>(SERVICE_OPERATION_TYPES);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isLegacyLedgerEntry = (value: unknown): value is AccountingOperationDto =>
  isRecord(value) &&
  ('balance_amount' in value || 'balanceAmount' in value) &&
  !('entries' in value);

const isLegacyGroup = (value: unknown): value is AccountingOperationDto[] =>
  Array.isArray(value) && value.length > 0 && isLegacyLedgerEntry(value[0]);

const isServiceOperationDto = (value: unknown): value is ServiceAccountingOperationDto =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.type === 'string' &&
  'entries' in value;

const parseOperationType = (value: unknown): ServiceAccountingOperationType => {
  if (typeof value === 'string' && OPERATION_TYPES.has(value)) {
    return value as ServiceAccountingOperationType;
  }

  return 'adjustment';
};

const legacyGroupToDto = (
  group: AccountingOperationDto[],
  index: number,
): ServiceAccountingOperationDto => {
  const sorted = [...group].sort((left, right) => {
    const leftTime = parseServiceDate(left.createdAt)?.getTime() ?? 0;
    const rightTime = parseServiceDate(right.createdAt)?.getTime() ?? 0;
    return leftTime - rightTime;
  });

  const invoiceId = sorted.find((entry) => entry.invoiceId)?.invoiceId ?? null;
  const netSum = sorted.reduce((total, entry) => total + Number(entry.sum ?? 0), 0);
  const fallbackDate = new Date().toISOString();
  const timeFrom = toSafeIsoString(sorted[0]?.createdAt, fallbackDate);
  const timeTo = toSafeIsoString(sorted[sorted.length - 1]?.createdAt, timeFrom);
  const operationId =
    invoiceId != null
      ? `invoice:${invoiceId}`
      : `legacy:${sorted.map((entry) => entry.id).join('-') || index}`;

  return {
    id: operationId,
    type: netSum < 0 ? 'withdraw' : 'deposit',
    displayAmount: String(Math.abs(netSum)),
    invoiceId,
    assetLabel: null,
    network: null,
    timeFrom,
    timeTo,
    relatedOperationIds: [],
    entries: sorted.map((entry, entryIndex) => ({
      id: entry.id ?? `${operationId}-${entryIndex}`,
      sum: entry.sum ?? 0,
      balanceAmount: entry.balance_amount ?? '0',
      comment: entry.comment ?? null,
      userId: entry.userId ?? null,
      walletId: entry.walletId ?? entry.wallet?.id ?? entryIndex,
      invoiceId: entry.invoiceId ?? null,
      createdAt: toSafeIsoString(entry.createdAt, timeFrom),
      lineTag: null,
      assetLabel: entry.assetLabel ?? null,
      network: entry.network ?? null,
      assetBalanceAmount: entry.assetBalanceAmount ?? null,
      wallet: {
        id: entry.wallet?.id ?? entry.walletId ?? entryIndex,
        name: entry.wallet?.name || '—',
        kind: parseWalletKind(
          (entry.wallet as { kind?: string | null } | undefined)?.kind ?? null,
        ),
      },
    })),
    comments: [],
  };
};

/** Tolerates new-accounting DTO and legacy grouped ledger rows from old /service. */
export const parseApiOperation = (
  raw: unknown,
  index = 0,
): ServiceAccountingOperationDto | null => {
  if (isLegacyGroup(raw)) {
    return legacyGroupToDto(raw, index);
  }

  if (isLegacyLedgerEntry(raw)) {
    return legacyGroupToDto([raw], index);
  }

  if (!isServiceOperationDto(raw)) {
    return null;
  }

  const fallbackDate = new Date().toISOString();

  return {
    ...raw,
    id: raw.id || `operation:${index}`,
    type: parseOperationType(raw.type),
    displayAmount: raw.displayAmount ?? '0',
    timeFrom: toSafeIsoString(raw.timeFrom, fallbackDate),
    timeTo: toSafeIsoString(raw.timeTo, toSafeIsoString(raw.timeFrom, fallbackDate)),
    relatedOperationIds: Array.isArray(raw.relatedOperationIds) ? raw.relatedOperationIds : [],
    network: raw.network ?? null,
    entries: Array.isArray(raw.entries) ? raw.entries : [],
    comments: Array.isArray(raw.comments) ? raw.comments : [],
  };
};
